import React from "react";
import { Button } from "@/components/ui/button";
import { CheckCircle } from "@phosphor-icons/react";
import { STATUS_META } from "./constants";

/**
 * UploadPreviewTable — renders the counters strip + first 150 preview rows
 * and the primary "Terapkan" action. Parent owns commit state.
 */
export default function UploadPreviewTable({
  uploadPreview,
  applyingUpload,
  onCancel,
  onApply,
}) {
  if (!uploadPreview) return null;
  const c = uploadPreview.counters || {};
  const applyDisabled =
    applyingUpload || (c.would_update === 0 && c.would_mismatch === 0);

  return (
    <div className="mt-4 border border-border bg-card p-4" data-testid="excel-upload-preview">
      <div className="flex flex-wrap gap-2 mb-3 text-xs font-mono">
        <span className="px-2 py-1 border border-emerald-500 bg-emerald-50 text-emerald-800 font-bold">
          {c.would_update} akan update
        </span>
        <span className="px-2 py-1 border border-red-500 bg-red-50 text-red-800 font-bold">
          {c.would_mismatch} mismatch
        </span>
        <span className="px-2 py-1 border border-blue-500 bg-blue-50 text-blue-800 font-bold">
          {c.already_verified} sudah verified
        </span>
        <span className="px-2 py-1 border border-amber-500 bg-amber-50 text-amber-800 font-bold">
          {c.not_found} tidak ditemukan
        </span>
        <span className="px-2 py-1 border border-border bg-secondary text-muted-foreground font-bold">
          {c.skipped} skipped
        </span>
        <span className="px-2 py-1 border border-border bg-secondary text-muted-foreground">
          {c.unchanged} tidak berubah
        </span>
      </div>
      <div className="max-h-96 overflow-y-auto border border-border">
        <table className="w-full text-xs">
          <thead className="bg-secondary sticky top-0">
            <tr>
              <th className="p-2 text-left overline">Row</th>
              <th className="p-2 text-left overline">Status</th>
              <th className="p-2 text-left overline">No SPB</th>
              <th className="p-2 text-left overline">Plat / Jumlah / Barang</th>
              <th className="p-2 text-left overline">Info</th>
            </tr>
          </thead>
          <tbody>
            {uploadPreview.predictions.slice(0, 150).map((p) => {
              const meta = STATUS_META[p.status] || STATUS_META.unchanged;
              return (
                <tr key={`row-${p.row}`} className="border-t border-border">
                  <td className="p-2 font-mono">{p.row}</td>
                  <td className="p-2">
                    <span className={`px-1.5 py-0.5 border text-[10px] font-bold uppercase ${meta.cls}`}>{meta.label}</span>
                  </td>
                  <td className="p-2 font-mono">{p.mapped?.no_spb || "-"}</td>
                  <td className="p-2 font-mono text-[11px]">
                    {p.mapped?.no_polisi} / {p.mapped?.jumlah} / {p.mapped?.nama_barang}
                  </td>
                  <td className="p-2 text-[11px] text-muted-foreground">{p.message || (p.mismatch_reasons || []).join("; ")}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {uploadPreview.predictions.length > 150 && (
        <div className="text-xs text-muted-foreground mt-2 italic">
          Menampilkan 150 dari {uploadPreview.predictions.length} baris di preview. Klik &quot;Terapkan&quot; untuk apply semua.
        </div>
      )}
      <div className="mt-4 flex gap-2 justify-end">
        <Button variant="outline" onClick={onCancel} className="rounded-none" data-testid="excel-upload-cancel">
          Batal
        </Button>
        <Button
          onClick={onApply}
          disabled={applyDisabled}
          className="rounded-none font-bold uppercase tracking-wider"
          data-testid="excel-upload-apply"
        >
          <CheckCircle size={16} className="mr-2" />
          {applyingUpload ? "Menerapkan..." : `Terapkan ${(c.would_update || 0) + (c.would_mismatch || 0)} Update`}
        </Button>
      </div>
    </div>
  );
}
